import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  FileText, 
  Star, 
  Clock, 
  BookTemplate,
  ChevronLeft,
  ChevronRight,
  Plus,
  Gift,
  MessageCircle,
  User,
  ChevronDown,
  Folder,
  Cpu
} from 'lucide-react';
import { AppLogo } from './AppLogo';
import { useAuth } from '../../hooks/useAuth';

interface SimpleSidebarProps {
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  className?: string;
}

interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  exact?: boolean;
  badge?: number;
}

const navItems: NavItem[] = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, exact: true },
  { name: 'All Prompts', href: '/dashboard', icon: FileText },
  { name: 'Favorites', href: '/dashboard?filter=favorites', icon: Star },
  { name: 'Recent', href: '/dashboard?filter=recent', icon: Clock },
  { name: 'Templates', href: '/dashboard?filter=templates', icon: BookTemplate },
  { name: 'Listing Templates', href: '/listing-templates', icon: Cpu },
  { name: 'Folders', href: '/dashboard?filter=folders', icon: Folder },
];

export const SimpleSidebar: React.FC<SimpleSidebarProps> = ({ 
  isCollapsed, 
  onToggleCollapse,
  className = ''
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);

  const isActive = (href: string, exact = false) => {
    if (exact) {
      return location.pathname === href && !location.search;
    }
    
    // Handle query parameter based routes
    if (href.includes('?filter=')) {
      const [path, query] = href.split('?');
      return location.pathname === path && location.search === `?${query}`;
    }
    
    return location.pathname.startsWith(href);
  };

  return (
    <div className={`
      ${isCollapsed ? 'w-16' : 'w-60'} 
      h-full bg-gradient-to-br from-modular-canvas-fog via-modular-ivory-mist to-modular-lunar-sage dark:bg-dark-gradient border-r border-gray-200 dark:border-dark-border
      flex flex-col transition-all duration-300 ease-in-out relative
      ${className}
    `}>
      
      {/* Logo Section - Always at top */}
      <div className={`
        flex items-center pt-4 pb-6 border-b border-gray-200 dark:border-dark-border cursor-pointer
        hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-200
        ${isCollapsed ? 'px-2' : 'px-6'}
      `}
        onClick={() => navigate('/dashboard')}
        title="Go to Dashboard"
      >
        {isCollapsed ? (
          <div className="flex items-center justify-center w-full">
            <AppLogo size="sm" animated={false} />
          </div>
        ) : (
          <div className="flex items-center space-x-3">
            <AppLogo size="sm" animated={false} />
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-dark-text">
                PromptOS
              </h1>
              <p className="text-sm text-modular-accent-blue dark:text-modular-accent-blue font-medium -mt-1">
                AI Prompt OS
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Toggle Button */}
      <button
        onClick={onToggleCollapse}
        className="
          absolute -right-3 top-6 w-6 h-6 bg-white dark:bg-dark-surface 
          border border-gray-200 dark:border-dark-border rounded-full
          flex items-center justify-center
          hover:bg-gray-50 dark:hover:bg-dark-hover
          transition-colors duration-200 z-10
        "
      >
        {isCollapsed ? (
          <ChevronRight className="h-3 w-3 text-gray-600 dark:text-gray-400" />
        ) : (
          <ChevronLeft className="h-3 w-3 text-gray-600 dark:text-gray-400" />
        )}
      </button>

      {/* Main Navigation */}
      <nav className="flex-1 p-3 mt-2">
        <ul className="space-y-1">
          {navItems.map((item) => {
            const isItemActive = isActive(item.href, item.exact);
            
            return (
              <li key={item.name}>
                <button
                  onClick={() => navigate(item.href)}
                  className={`
                    w-full flex items-center ${isCollapsed ? 'justify-center px-3' : 'px-4'} 
                    py-3 text-sm font-medium rounded-xl transition-all duration-300 text-left 
                    group relative ${
                      isItemActive
                        ? 'bg-modular-accent-blue text-white shadow-md'
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100/80 dark:hover:bg-dark-hover hover:text-modular-accent-blue dark:hover:text-modular-accent-blue'
                    }
                  `}
                  title={isCollapsed ? item.name : undefined}
                >
                  <item.icon className={`${isCollapsed ? 'h-5 w-5' : 'h-5 w-5 mr-3'} flex-shrink-0`} />
                  {!isCollapsed && (
                    <span className="truncate">{item.name}</span>
                  )}
                  {!isCollapsed && item.badge && (
                    <span className="ml-auto bg-modular-accent-rose text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-semibold">
                      {item.badge}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Bottom Section */}
      <div className={`border-t border-gray-200 dark:border-dark-border ${isCollapsed ? 'p-2' : 'p-4'}`}>
        <div className="space-y-2">
          {/* Promotional Section */}
          {!isCollapsed && (
            <div className="mb-4">
              <button
                className="w-full flex items-center px-3 py-2 text-sm text-modular-accent-rose dark:text-modular-accent-rose 
                  hover:bg-orange-50 dark:hover:bg-orange-900/20 rounded-lg transition-all duration-300 text-left"
              >
                <Gift className="h-4 w-4 mr-3 flex-shrink-0" />
                <span className="font-medium">Upgrade to Pro</span>
              </button>
            </div>
          )}

          {/* Help & Support */}
          <button
            className={`
              w-full flex items-center ${isCollapsed ? 'justify-center px-2 py-3' : 'px-3 py-2'} 
              text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100/80 
              dark:hover:bg-dark-hover rounded-lg transition-all duration-300 text-left
            `}
            title={isCollapsed ? 'Help & Support' : undefined}
          >
            <MessageCircle className={`${isCollapsed ? 'h-5 w-5' : 'h-4 w-4 mr-3'} flex-shrink-0`} />
            {!isCollapsed && <span>Help & Support</span>}
          </button>

          {/* User Profile */}
          <div className="relative">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className={`
                w-full flex items-center ${isCollapsed ? 'justify-center px-2 py-3' : 'px-3 py-2'} 
                text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100/80 
                dark:hover:bg-dark-hover rounded-lg transition-all duration-300 text-left
              `}
              title={isCollapsed ? user?.name || 'User Profile' : undefined}
            >
              <User className={`${isCollapsed ? 'h-5 w-5' : 'h-4 w-4 mr-3'} flex-shrink-0`} />
              {!isCollapsed && (
                <div className="flex items-center justify-between flex-1">
                  <span className="truncate">{user?.name || 'User Profile'}</span>
                  <ChevronDown className="h-4 w-4 ml-2" />
                </div>
              )}
            </button>

            {/* User Dropdown - Only show when expanded */}
            {showUserMenu && !isCollapsed && (
              <div className="absolute bottom-full left-0 right-0 mb-2 bg-white dark:bg-dark-surface 
                rounded-lg shadow-lg border border-gray-200 dark:border-dark-border py-2">
                <div className="px-3 py-2 border-b border-gray-200 dark:border-dark-border">
                  <p className="text-sm font-medium text-gray-900 dark:text-dark-text">
                    {user?.name || 'User'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {user?.email || 'No email'}
                  </p>
                </div>
                <button className="w-full text-left px-3 py-2 text-sm text-gray-600 dark:text-gray-300 
                  hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                  Settings
                </button>
                <button className="w-full text-left px-3 py-2 text-sm text-red-600 dark:text-red-400 
                  hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

interface FloatingNewButtonProps {
  onClick: () => void;
}

export const FloatingNewButton: React.FC<FloatingNewButtonProps> = ({ onClick }) => {
  return (
    <button
      onClick={onClick}
      className="
        fixed bottom-4 right-4 lg:bottom-8 lg:right-8
        w-12 h-12 lg:w-16 lg:h-16
        bg-modular-primary-gradient
        rounded-full shadow-2xl
        transform hover:scale-110 active:scale-95 group
        flex items-center justify-center
        transition-all duration-300 ease-out
        hover:shadow-modular-accent-blue/30
        z-50
      "
    >
      <div className="absolute inset-0 bg-modular-secondary-gradient 
        opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-full" />
      <Plus className="h-5 w-5 lg:h-8 lg:w-8 relative z-10 group-hover:rotate-90 transition-transform duration-300" />
      <div className="absolute inset-0 rounded-full bg-modular-frosted-indigo opacity-20 
        animate-ping group-hover:animate-none" />
    </button>
  );
};